import { type FileUploadResult, useS3Client } from '@contember/bindx-uploader'
import { type ChangeEvent, type DragEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getUploadErrorMessage, UPLOAD_PREFIX } from './upload'

export type UploadState =
	| { status: 'idle' }
	| { status: 'uploading'; fileName: string; progress: number }
	| { status: 'error'; fileName?: string; error: string }

export type UploadedFile = {
	file: File
	url: string
	fileName: string
	fileSize: number
	mimeType: string
}

/**
 * One S3 upload at a time (bindx-uploader `useS3Client`, prefix `assets/`).
 * `accept` returns an end-user message when the file must be refused;
 * `onUploaded` runs after the upload — it may be async (image dimensions)
 * and its result is handed to the caller through `onDone`.
 */
export function useUpload({ accept, onUploaded, prefix = UPLOAD_PREFIX }: {
	accept?: (file: File) => string | null
	onUploaded: (uploaded: UploadedFile) => Promise<void> | void
	prefix?: string
}) {
	const options = useMemo(() => ({}), [])
	const client = useS3Client(options)
	const inputRef = useRef<HTMLInputElement | null>(null)
	const abortRef = useRef<AbortController | null>(null)
	const [state, setState] = useState<UploadState>({ status: 'idle' })

	useEffect(() => () => abortRef.current?.abort(), [])

	const upload = useCallback(async (file: File): Promise<void> => {
		const refusal = accept?.(file) ?? null
		if (refusal) {
			setState({ status: 'error', fileName: file.name, error: refusal })
			return
		}

		abortRef.current?.abort()
		const controller = new AbortController()
		abortRef.current = controller
		setState({ status: 'uploading', fileName: file.name, progress: 0 })

		try {
			const result = await uploadWithFetchFallback(client, {
				file,
				signal: controller.signal,
				prefix,
				onProgress: progress => setState({ status: 'uploading', fileName: file.name, progress }),
			})
			await onUploaded({
				file,
				url: result.publicUrl,
				fileName: file.name,
				fileSize: file.size,
				mimeType: file.type || 'application/octet-stream',
			})
			setState({ status: 'idle' })
		} catch (error) {
			if (controller.signal.aborted) {
				setState({ status: 'idle' })
				return
			}
			setState({ status: 'error', fileName: file.name, error: getUploadErrorMessage(error) })
		} finally {
			if (abortRef.current === controller) abortRef.current = null
		}
	}, [accept, client, onUploaded, prefix])

	const cancel = useCallback(() => abortRef.current?.abort(), [])
	const openPicker = useCallback(() => inputRef.current?.click(), [])

	const onInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
		const file = event.currentTarget.files?.[0]
		if (!file) return
		// Keep the FileList alive until the request has consumed the File. This is
		// observable in automated Chromium where clearing the input first can
		// invalidate the backing file while an XHR/fetch upload is still streaming.
		void upload(file).finally(() => {
			if (inputRef.current) inputRef.current.value = ''
		})
	}, [upload])

	const onDrop = useCallback((event: DragEvent<HTMLElement>) => {
		event.preventDefault()
		const file = event.dataTransfer.files[0]
		if (file) void upload(file)
	}, [upload])

	const onDragOver = useCallback((event: DragEvent<HTMLElement>) => {
		event.preventDefault()
	}, [])

	return { state, upload, cancel, openPicker, inputRef, onInputChange, onDrop, onDragOver }
}

async function uploadWithFetchFallback(
	client: ReturnType<typeof useS3Client>,
	{
		file,
		signal,
		prefix,
		onProgress,
	}: {
		file: File
		signal: AbortSignal
		prefix: string
		onProgress: (progress: number) => void
	},
): Promise<FileUploadResult> {
	// Snapshot the bytes before the XHR path. Some automated Chromium file
	// handles become unusable after a failed XHR even though the File metadata
	// remains available; a Blob copy keeps the retry body independent.
	const fallbackBody = new Blob([file], { type: file.type })
	try {
		return await client.upload({
			file,
			signal,
			prefix,
			fileName: file.name,
			onProgress: progress => onProgress(Math.round(progress.progress * 100)),
		})
	} catch (error) {
		if (signal.aborted) throw error
		// Chromium can report an opaque XHR network error for an otherwise valid
		// local S3 PUT. Re-sign and retry with fetch; production keeps the normal
		// progress-reporting XHR path unless that transport fails.
		const signed = await client.options.signUrl({
			contentType: file.type,
			file,
			fileName: file.name,
			prefix,
		})
		const response = await fetch(signed.url, {
			body: fallbackBody,
			headers: Object.fromEntries(signed.headers.map(header => [header.key, header.value])),
			method: signed.method,
			signal,
		})
		if (!response.ok) throw new Error(`Upload selhal: HTTP ${response.status}.`)
		onProgress(100)
		return { publicUrl: signed.publicUrl }
	}
}
